import { useGetAdminPending } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useCallback, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { Pencil, X, Settings2, Zap, Star, Trash2 } from "lucide-react";
import { useCategories } from "@/hooks/use-categories";

type ContentType = "deals" | "businesses" | "events" | "news" | "reviews" | "jobs" | "job_vacancies" | "seeker_profiles";

interface PendingItem {
  id: number;
  title?: string;
  name?: string;
  businessName?: string;
  companyName?: string;
  description?: string;
  content?: string;
  town?: string;
  category?: string;
  venue?: string;
  eventDate?: string;
  organiserName?: string;
  discount?: string;
  phone?: string;
  urgency?: string;
  budgetRange?: string;
  contactName?: string;
  employmentType?: string;
  salary?: string;
  bio?: string;
  // Review-specific fields
  reviewerName?: string;
  reviewText?: string;
  rating?: number;
  targetType?: string;
  targetId?: number;
  targetName?: string | null;
  [key: string]: unknown;
}

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const CONTENT_LABELS: Record<string, string> = {
  jobs: "Quote Requests",
  job_vacancies: "Job Vacancies",
  seeker_profiles: "Looking for Work",
  businesses: "Businesses",
  deals: "Deals",
  events: "Events",
  news: "News",
  reviews: "Reviews",
};

const URGENCY_LABELS: Record<string, { label: string; colour: string }> = {
  emergency: { label: "Emergency", colour: "bg-red-100 text-red-700" },
  within_24h: { label: "Within 24h", colour: "bg-orange-100 text-orange-700" },
  this_week: { label: "This Week", colour: "bg-amber-100 text-amber-700" },
  this_month: { label: "This Month", colour: "bg-blue-100 text-blue-700" },
  flexible: { label: "Flexible", colour: "bg-slate-100 text-slate-600" },
};

type ApprovalMap = Record<string, boolean>;

function ApprovalSettings() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<ApprovalMap>({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetch("/api/admin/approval-settings", { headers: authHeaders() })
      .then(r => r.json())
      .then((data: ApprovalMap) => setSettings(data))
      .catch(() => {});
  }, []);

  const toggle = async (type: string) => {
    const next = { ...settings, [type]: !settings[type] };
    setSettings(next);
    setSaving(true);
    try {
      await fetch("/api/admin/approval-settings", {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ [type]: next[type] }),
      });
    } catch {
      toast({ title: "Failed to save setting", variant: "destructive" });
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="py-0">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between py-3 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Settings2 size={15} className="text-slate-400" />
            Auto-Approval Settings
          </span>
          <span className="text-xs text-slate-400">{open ? "Hide" : "Configure"}</span>
        </button>

        {open && (
          <div className="border-t border-slate-100 pb-4 pt-3 space-y-1">
            <p className="text-xs text-slate-500 mb-3">
              When enabled, submissions go live immediately without manual review.
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Object.keys(CONTENT_LABELS).map((type) => {
                const isOn = !!settings[type];
                return (
                  <label
                    key={type}
                    className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                      isOn ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      {isOn && <Zap size={13} className="text-emerald-600" />}
                      {CONTENT_LABELS[type]}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${isOn ? "text-emerald-600" : "text-slate-400"}`}>
                        {isOn ? "Auto" : "Manual"}
                      </span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isOn}
                          disabled={saving}
                          onChange={() => void toggle(type)}
                        />
                        <div
                          onClick={() => !saving && void toggle(type)}
                          className={`w-9 h-5 rounded-full transition-colors cursor-pointer ${isOn ? "bg-emerald-500" : "bg-slate-300"}`}
                        >
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isOn ? "translate-x-4" : ""}`} />
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const TYPE_ENDPOINT: Record<ContentType, string> = {
  jobs: "admin/content/jobs",
  job_vacancies: "admin/content/job-vacancies",
  seeker_profiles: "admin/content/seeker-profiles",
  deals: "deals",
  businesses: "businesses",
  events: "events",
  news: "news",
  reviews: "admin/content/reviews",
};

function CategorySelect({ value, onChange, cats }: { value: string; onChange: (v: string) => void; cats: string[] }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="h-8 text-sm w-full border border-input rounded-md px-2 bg-background"
    >
      <option value="">— Select category —</option>
      {cats.map(c => <option key={c} value={c}>{c}</option>)}
      {value && !cats.includes(value) && <option value={value}>{value}</option>}
    </select>
  );
}

function EditForm({
  item,
  type,
  onSaved,
  onCancel,
}: {
  item: PendingItem;
  type: ContentType;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const businessCats = useCategories("business");
  const dealCats = useCategories("deal");
  const eventCats = useCategories("event");
  const newsCats = useCategories("news");
  const jobCats = useCategories("job");
  const catMap: Partial<Record<ContentType, string[]>> = {
    businesses: businessCats,
    deals: dealCats,
    events: eventCats,
    news: newsCats,
    jobs: jobCats,
    job_vacancies: jobCats,
    seeker_profiles: jobCats,
  };
  const cats = catMap[type] ?? [];
  const [form, setForm] = useState<Record<string, string>>({
    title: item.title ?? "",
    name: item.name ?? "",
    businessName: item.businessName ?? "",
    companyName: item.companyName ?? "",
    description: item.description ?? item.content ?? item.bio ?? "",
    category: item.category ?? "",
    town: item.town ?? "",
    venue: item.venue ?? "",
    eventDate: item.eventDate ?? "",
    organiserName: item.organiserName ?? "",
    discount: item.discount ?? "",
    phone: item.phone ?? "",
    urgency: item.urgency ?? "",
    budgetRange: item.budgetRange ?? "",
    contactName: item.contactName ?? "",
    salary: item.salary ?? "",
    employmentType: item.employmentType ?? "",
    reviewText: item.reviewText ?? "",
    rating: String(item.rating ?? ""),
  });
  const [saving, setSaving] = useState(false);

  const f = (k: string) => form[k] ?? "";
  const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const endpoint = TYPE_ENDPOINT[type];
      const payload: Record<string, string | number> = {};
      if (type === "deals") {
        payload.title = f("title");
        payload.businessName = f("businessName");
        payload.description = f("description");
        payload.category = f("category");
        payload.town = f("town");
        payload.discount = f("discount");
      } else if (type === "events") {
        payload.name = f("name");
        payload.description = f("description");
        payload.category = f("category");
        payload.town = f("town");
        payload.venue = f("venue");
        payload.eventDate = f("eventDate");
        payload.organiserName = f("organiserName");
      } else if (type === "news") {
        payload.title = f("title");
        payload.content = f("description");
        payload.category = f("category");
        payload.town = f("town");
      } else if (type === "businesses") {
        payload.name = f("name");
        payload.description = f("description");
        payload.category = f("category");
        payload.town = f("town");
        payload.phone = f("phone");
      } else if (type === "jobs") {
        payload.title = f("title");
        payload.description = f("description");
        payload.category = f("category");
        payload.town = f("town");
        payload.budgetRange = f("budgetRange");
        payload.urgency = f("urgency");
        payload.contactName = f("contactName");
        payload.phone = f("phone");
      } else if (type === "job_vacancies") {
        payload.title = f("title");
        payload.companyName = f("companyName");
        payload.description = f("description");
        payload.category = f("category");
        payload.town = f("town");
        payload.salary = f("salary");
        payload.employmentType = f("employmentType");
      } else if (type === "seeker_profiles") {
        payload.name = f("name");
        payload.bio = f("description");
        payload.category = f("category");
        payload.town = f("town");
        payload.employmentType = f("employmentType");
      } else if (type === "reviews") {
        payload.reviewText = f("reviewText");
        payload.rating = Number(f("rating"));
      }
      const res = await fetch(`/api/${endpoint}/${item.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Changes saved" });
      onSaved();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }, [form, item.id, type, toast, onSaved]);

  return (
    <div className="mt-3 border-t border-slate-100 pt-3 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {(type === "deals") && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Title</label>
              <Input value={f("title")} onChange={e => set("title", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Business Name</label>
              <Input value={f("businessName")} onChange={e => set("businessName", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <CategorySelect value={f("category")} onChange={v => set("category", v)} cats={cats} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={f("town")} onChange={e => set("town", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Discount</label>
              <Input value={f("discount")} onChange={e => set("discount", e.target.value)} className="h-8 text-sm" />
            </div>
          </>
        )}
        {(type === "events") && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Event Name</label>
              <Input value={f("name")} onChange={e => set("name", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <CategorySelect value={f("category")} onChange={v => set("category", v)} cats={cats} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={f("town")} onChange={e => set("town", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Venue</label>
              <Input value={f("venue")} onChange={e => set("venue", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Event Date</label>
              <Input value={f("eventDate")} onChange={e => set("eventDate", e.target.value)} className="h-8 text-sm" placeholder="YYYY-MM-DD" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Organiser</label>
              <Input value={f("organiserName")} onChange={e => set("organiserName", e.target.value)} className="h-8 text-sm" />
            </div>
          </>
        )}
        {(type === "news") && (
          <>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Title</label>
              <Input value={f("title")} onChange={e => set("title", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <CategorySelect value={f("category")} onChange={v => set("category", v)} cats={cats} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={f("town")} onChange={e => set("town", e.target.value)} className="h-8 text-sm" />
            </div>
          </>
        )}
        {(type === "businesses") && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Business Name</label>
              <Input value={f("name")} onChange={e => set("name", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <CategorySelect value={f("category")} onChange={v => set("category", v)} cats={cats} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={f("town")} onChange={e => set("town", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Phone</label>
              <Input value={f("phone")} onChange={e => set("phone", e.target.value)} className="h-8 text-sm" />
            </div>
          </>
        )}
        {(type === "jobs") && (
          <>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Title</label>
              <Input value={f("title")} onChange={e => set("title", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <CategorySelect value={f("category")} onChange={v => set("category", v)} cats={cats} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={f("town")} onChange={e => set("town", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Budget Range</label>
              <Input value={f("budgetRange")} onChange={e => set("budgetRange", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Urgency</label>
              <Input value={f("urgency")} onChange={e => set("urgency", e.target.value)} className="h-8 text-sm" placeholder="flexible / this_week / emergency…" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Contact Name</label>
              <Input value={f("contactName")} onChange={e => set("contactName", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Phone</label>
              <Input value={f("phone")} onChange={e => set("phone", e.target.value)} className="h-8 text-sm" />
            </div>
          </>
        )}
        {(type === "job_vacancies") && (
          <>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Job Title</label>
              <Input value={f("title")} onChange={e => set("title", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Company Name</label>
              <Input value={f("companyName")} onChange={e => set("companyName", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <CategorySelect value={f("category")} onChange={v => set("category", v)} cats={cats} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={f("town")} onChange={e => set("town", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Salary</label>
              <Input value={f("salary")} onChange={e => set("salary", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Employment Type</label>
              <select value={f("employmentType")} onChange={e => set("employmentType", e.target.value)} className="h-8 text-sm w-full border border-input rounded-md px-2 bg-background">
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="temporary">Temporary</option>
                <option value="apprenticeship">Apprenticeship</option>
              </select>
            </div>
          </>
        )}
        {(type === "seeker_profiles") && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Name</label>
              <Input value={f("name")} onChange={e => set("name", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
              <CategorySelect value={f("category")} onChange={v => set("category", v)} cats={cats} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
              <Input value={f("town")} onChange={e => set("town", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Employment Type</label>
              <select value={f("employmentType")} onChange={e => set("employmentType", e.target.value)} className="h-8 text-sm w-full border border-input rounded-md px-2 bg-background">
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="temporary">Temporary</option>
                <option value="apprenticeship">Apprenticeship</option>
              </select>
            </div>
          </>
        )}
        {(type === "reviews") && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Rating (1–5)</label>
              <Input type="number" min={1} max={5} value={f("rating")} onChange={e => set("rating", e.target.value)} className="h-8 text-sm" />
            </div>
          </>
        )}
      </div>
      {type !== "reviews" && (
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">
            {type === "seeker_profiles" ? "Bio" : "Description"}
          </label>
          <Textarea value={f("description")} onChange={e => set("description", e.target.value)} rows={3} className="text-sm" />
        </div>
      )}
      {type === "reviews" && (
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Review Text</label>
          <Textarea value={f("reviewText")} onChange={e => set("reviewText", e.target.value)} rows={3} className="text-sm" />
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" disabled={saving} onClick={handleSave} className="bg-primary hover:bg-primary/90 h-7 text-xs px-3">
          {saving ? "Saving…" : "Save Changes"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="h-7 text-xs px-3">
          Cancel
        </Button>
      </div>
    </div>
  );
}

function PendingSection({
  title,
  items,
  type,
  onApprove,
  onReject,
  onRefetch,
  busy,
}: {
  title: string;
  items: PendingItem[];
  type: ContentType;
  onApprove: (type: ContentType, id: number) => void;
  onReject: (type: ContentType, id: number) => void;
  onRefetch: () => void;
  busy: Set<string>;
}) {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<number | null>(null);
  if (!items.length) return null;

  const handleDeleteReview = async (id: number) => {
    try {
      const res = await fetch(`/api/admin/content/reviews/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Review deleted" });
      onRefetch();
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-primary border-b pb-2">{title}</h2>
      {items.map((item) => {
        const label = item.title ?? item.name ?? item.businessName ?? item.companyName ?? `#${item.id}`;
        const key = `${type}-${item.id}`;
        const isEditing = editingId === item.id;
        return (
          <Card key={item.id}>
            <CardContent className="py-4">
              <div className="flex gap-4 items-start">
                <div className="flex-1 min-w-0">
                  {type === "reviews" ? (
                    <>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-semibold text-slate-800">{item.reviewerName ?? "Anonymous"}</p>
                        <span className="text-xs text-slate-400">reviewed</span>
                        <span className="font-medium text-primary text-sm">
                          {item.targetName ?? `${item.targetType ?? "unknown"} #${item.targetId ?? item.id}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 mb-2">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star
                            key={s}
                            size={14}
                            className={(item.rating ?? 0) >= s ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}
                          />
                        ))}
                        <span className="text-xs text-slate-500 ml-1">{item.rating ?? 0}/5</span>
                      </div>
                      {item.reviewText && !isEditing && (
                        <p className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 italic">
                          "{item.reviewText as string}"
                        </p>
                      )}
                      {isEditing && (
                        <EditForm
                          item={item}
                          type={type}
                          onSaved={() => { setEditingId(null); onRefetch(); }}
                          onCancel={() => setEditingId(null)}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold truncate">{label}</p>
                        {type === "jobs" && item.urgency && URGENCY_LABELS[item.urgency] && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${URGENCY_LABELS[item.urgency].colour}`}>
                            {URGENCY_LABELS[item.urgency].label}
                          </span>
                        )}
                        {(type === "job_vacancies" || type === "seeker_profiles") && item.employmentType && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 bg-blue-100 text-blue-700">
                            {item.employmentType.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                      {type === "job_vacancies" && item.companyName && !isEditing && (
                        <p className="text-xs font-medium text-primary mt-0.5">{item.companyName as string}</p>
                      )}
                      {(item.description ?? item.bio) && !isEditing && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{(item.description ?? item.bio) as string}</p>
                      )}
                      {(item.town || item.category || (type === "jobs" && item.budgetRange) || (type === "job_vacancies" && item.salary)) && !isEditing && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {[item.category, item.town, type === "jobs" && item.budgetRange ? `Budget: ${item.budgetRange}` : null, type === "job_vacancies" && item.salary ? `Salary: ${item.salary}` : null].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {type === "jobs" && item.contactName && !isEditing && (
                        <p className="text-xs text-muted-foreground mt-0.5">Contact: {item.contactName as string}</p>
                      )}
                      {isEditing && (
                        <EditForm
                          item={item}
                          type={type}
                          onSaved={() => { setEditingId(null); onRefetch(); }}
                          onCancel={() => setEditingId(null)}
                        />
                      )}
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                    <button
                      onClick={() => setEditingId(item.id)}
                      className="flex items-center gap-1 text-xs text-slate-500 font-medium px-2 py-1.5 rounded-lg border border-slate-200 hover:border-primary/40 hover:text-primary transition-colors"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                    {type === "reviews" && (
                      <button
                        onClick={() => void handleDeleteReview(item.id)}
                        className="flex items-center gap-1 text-xs text-red-500 font-medium px-2 py-1.5 rounded-lg border border-red-200 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    )}
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={busy.has(key)}
                      onClick={() => onApprove(type, item.id)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy.has(key)}
                      onClick={() => onReject(type, item.id)}
                    >
                      Reject
                    </Button>
                  </div>
                )}
                {isEditing && (
                  <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                    <X size={16} />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function AdminPending() {
  const { user, isLoading: authLoading } = useAuth();
  const { data, isLoading, refetch } = useGetAdminPending();
  const { toast } = useToast();
  const [busy, setBusy] = useState<Set<string>>(new Set());

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const handle = async (action: "approve" | "reject", type: ContentType, id: number) => {
    const key = `${type}-${id}`;
    setBusy((s) => new Set([...s, key]));
    try {
      const token = localStorage.getItem("ayrshire_token");
      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const res = await fetch(`/api/admin/content/${type}/${id}/${action}`, { method: "PATCH", headers });
      const body = await res.json() as { notificationResult?: { matchingTrades: number; tokensFound: number } };

      if (action === "approve" && type === "jobs" && body.notificationResult) {
        const { matchingTrades, tokensFound } = body.notificationResult;
        if (tokensFound > 0) {
          toast({ title: `Approved — ${tokensFound} tradesperson${tokensFound !== 1 ? "s" : ""} notified via push` });
        } else if (matchingTrades > 0) {
          toast({
            title: "Approved",
            description: `${matchingTrades} matching tradesperson${matchingTrades !== 1 ? "s" : ""} found but none have push notifications enabled yet.`,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Approved",
            description: "No tradespeople with a matching category found — nobody was notified.",
            variant: "destructive",
          });
        }
      }

      await refetch();
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  const onApprove = (type: ContentType, id: number) => handle("approve", type, id);
  const onReject = (type: ContentType, id: number) => handle("reject", type, id);

  const rawData = data as unknown as {
    jobs?: unknown[]; jobVacancies?: unknown[]; seekerProfiles?: unknown[];
    deals?: unknown[]; businesses?: unknown[]; events?: unknown[];
    news?: unknown[]; reviews?: unknown[];
  } | undefined;

  const totalPending =
    (rawData?.jobs?.length ?? 0) +
    (rawData?.jobVacancies?.length ?? 0) +
    (rawData?.seekerProfiles?.length ?? 0) +
    (rawData?.deals?.length ?? 0) +
    (rawData?.businesses?.length ?? 0) +
    (rawData?.events?.length ?? 0) +
    (rawData?.news?.length ?? 0) +
    (rawData?.reviews?.length ?? 0);

  return (
    <AdminLayout current="/admin/pending">
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-primary">Pending Approvals</h1>
          {!isLoading && (
            <span className={`text-sm font-semibold px-3 py-1 rounded-full ${totalPending > 0 ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground"}`}>
              {totalPending} pending
            </span>
          )}
        </div>

        <ApprovalSettings />

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : totalPending === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">Nothing waiting for approval. All clear.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            <PendingSection title="Quote Requests" items={(rawData?.jobs ?? []) as PendingItem[]} type="jobs" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
            <PendingSection title="Job Vacancies" items={(rawData?.jobVacancies ?? []) as PendingItem[]} type="job_vacancies" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
            <PendingSection title="Looking for Work" items={(rawData?.seekerProfiles ?? []) as PendingItem[]} type="seeker_profiles" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
            <PendingSection title="Businesses" items={(rawData?.businesses ?? []) as PendingItem[]} type="businesses" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
            <PendingSection title="Deals" items={(rawData?.deals ?? []) as PendingItem[]} type="deals" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
            <PendingSection title="Events" items={(rawData?.events ?? []) as PendingItem[]} type="events" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
            <PendingSection title="News" items={(rawData?.news ?? []) as PendingItem[]} type="news" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
            <PendingSection title="Reviews" items={(rawData?.reviews ?? []) as PendingItem[]} type="reviews" onApprove={onApprove} onReject={onReject} onRefetch={() => void refetch()} busy={busy} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
